/**
 * report/inputSummary.js — one description of the model's inputs, shared by the Review screen
 * and the PDF's Input Summary section.
 *
 * The point of the shared builder is that the printed report can never disagree with what the
 * user reviewed on screen, and a new wizard input appears in both places at once. Nothing here
 * renders: it returns plain data that a React table and the PDF table renderer both consume.
 *
 * Shape:
 *   [{ id, title, columns:[{key,label,align?}], rows:[{...}], note? }]
 *
 * A group with no rows is not returned at all, which is what lets the PDF omit empty groups
 * without a placeholder.
 */
import { polygonArea } from '../geometry.js';
import {
  getGlazingUValue, getGlazingSHGC, getGlazingVLT, getGlazingById,
  calcOverhangPF, calcFinPF,
} from '../cberdMaterials.js';
import { enumerateExteriorWalls, resolveWallEnvelope, eachEnvelopeContainer } from '../envelopeResolve.js';
import { getHvacType } from '../hvacSystems.js';
import { isDetailedSystem, detailedSummary } from '../hvacDetailed/model.js';
import { SCHEDULE_TYPES, SCHEDULE_TYPE_MAP, resolveSchedule, scheduleSource } from '../scheduleTemplates.js';
import { LOAD_TYPES } from '../loadDefinitions.js';

/**
 * Fixed-decimal string, or null when there is no number to show.
 *
 * The null/'' guard is load-bearing: `Number(null)` and `Number('')` are both 0, so without it
 * every unset field rendered as "0.00" and the `?? fallback` at each call site never fired. That
 * is how the fenestration table reported U = 0.00 for orientations using a library glazing (their
 * `custom_u` is null by design, see ElementPropertyModal.handleGlazingChange).
 */
const num = (v, d = 1) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(d) : null;
};
/** Indian digit grouping, matching the rest of the app and the PDF. */
const group = (v, d = 0) => Number(v).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
/** "interior_equipment" -> "Interior equipment", for ids with no label in the library. */
const humanise = (s) => String(s || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
const DIRS = ['N', 'E', 'S', 'W', 'NE', 'SE', 'SW', 'NW'];

/** Sorted key iteration everywhere, so the same model always yields the same row order. */
const sortedEntries = (obj) => Object.entries(obj || {}).sort(([a], [b]) => a.localeCompare(b));

const zoneArea = (z) => {
  try { return polygonArea(z.footprint || []); } catch { return 0; }
};

// ── groups ────────────────────────────────────────────────────────────────────

function geometry(b) {
  const floors = b.floors || [];
  if (!floors.length) return null;
  const rows = floors.map((f) => {
    const zones = f.zones || [];
    const cond = zones.filter((z) => z.is_conditioned !== false);
    const area = zones.reduce((s, z) => s + zoneArea(z), 0);
    const condArea = cond.reduce((s, z) => s + zoneArea(z), 0);
    return {
      floor: f.name || f.id,
      elevation: num(f.elevation, 2),
      height: num(f.height, 2),
      zones: String(zones.length),
      conditioned: String(cond.length),
      area: num(area, 1),
      condArea: num(condArea, 1),
    };
  });
  return {
    id: 'geometry',
    title: 'Geometry',
    columns: [
      { key: 'floor', label: 'Floor' },
      { key: 'elevation', label: 'Elevation (m)', align: 'right' },
      { key: 'height', label: 'Height (m)', align: 'right' },
      { key: 'zones', label: 'Zones', align: 'right' },
      { key: 'conditioned', label: 'Conditioned', align: 'right' },
      { key: 'area', label: 'Floor area (m²)', align: 'right' },
      { key: 'condArea', label: 'Conditioned (m²)', align: 'right' },
    ],
    rows,
  };
}

/**
 * Resolved glazing properties of one window definition.
 *
 * `custom_*` is only populated for a user-defined glazing; picking a library glazing sets all
 * three back to null and leaves `glazing_id` to carry the values, exactly as the RETV, EPF and
 * IDF paths resolve them. Reading `custom_*` alone therefore reports nothing for most windows.
 *
 * VLT is normalised to a 0-100 percentage: the library stores a fraction while a user-defined
 * glazing stores a percentage, and the IDF writer normalises the same way.
 */
export function resolveWindowGlazing(w, customGlazings = []) {
  const id = w?.glazing_id || w?.glazingId || null;
  const lib = id ? getGlazingById(id) : null;
  const own = id ? (customGlazings || []).find((g) => g && g.id === id) : null;
  const u = w?.custom_u ?? (lib ? getGlazingUValue(id) : null);
  const shgc = w?.custom_shgc ?? (lib ? getGlazingSHGC(id) : null);
  const rawVlt = w?.custom_vlt;
  const vltPct = rawVlt != null && Number.isFinite(Number(rawVlt))
    ? (Number(rawVlt) > 1 ? Number(rawVlt) : Number(rawVlt) * 100)
    : (lib ? getGlazingVLT(id) : null);
  return { id, name: lib?.name || own?.name || null, u, shgc, vltPct };
}

/**
 * One value for a set of windows: the shared value formatted, or 'Mixed' when they disagree.
 * An orientation can carry up to five window definitions, so reporting only the first would
 * quietly hide a second glazing spec.
 */
function sharedNum(items, pick, fmt) {
  const vals = items.map(pick);
  if (!vals.length || vals.every((v) => v == null)) return null;
  const first = vals[0];
  const same = vals.every((v) => (v == null ? first == null : Math.abs(Number(v) - Number(first)) < 1e-9));
  if (!same) return 'Mixed';
  return first == null ? null : fmt(first);
}

/** Same idea for a text field (the glazing name). */
function sharedText(items, pick) {
  const names = [...new Set(items.map(pick).filter(Boolean))];
  if (names.length === 1) return names[0];
  return names.length > 1 ? 'Mixed' : null;
}

/**
 * Real opening areas per orientation, resolved the same way the RETV, EPF and IDF paths do:
 * every exterior wall is enumerated, its own fenestration cascade resolved, and the window
 * area taken from explicit dimensions where the user gave them and from the ratio otherwise.
 * A WWR alone does not tell a reviewer how much glass the building actually has.
 */
export function orientationWindowAreas(b) {
  const map = {};
  try {
    for (const { floor, zone, wallId, dir, grossArea } of enumerateExteriorWalls(b)) {
      const fen = resolveWallEnvelope(b, floor, zone, wallId, dir).fen;
      const d = map[dir] || (map[dir] = { wall: 0, win: 0 });
      d.wall += grossArea;
      let winA = 0;
      for (const w of (fen.windows || [])) {
        if (!w) continue;
        winA += w.method === 'dimensions'
          ? (w.width || 1.2) * (w.height || 1.5) * (w.count || 0)
          : ((w.wwr || 0) / 100) * grossArea;
      }
      // The same 95% guard the Review screen applies: a wall cannot be more than glass.
      d.win += Math.min(winA, grossArea * 0.95);
    }
  } catch {
    return {};                       // an incomplete model reports no area rather than throwing
  }
  return map;
}

/**
 * What is shading the windows of one orientation, and by how much.
 *
 * Projection factor is the code's own measure of a shade's depth against the opening it
 * protects (ENS 2024 / ECBC), so it is computed here with the same helpers the EPF engine uses
 * rather than reported as a raw depth, which would mean nothing without the window height.
 */
function orientationShading(wins) {
  const kinds = new Set();
  const pfs = [];
  for (const w of wins) {
    const h = w?.height || 1.5;
    const o = w?.overhang || {};
    const fl = w?.fin_left || {};
    const fr = w?.fin_right || {};
    if (o.depth > 0) { kinds.add('Overhang'); pfs.push(calcOverhangPF(o.depth, o.gap, h)); }
    if (fl.depth > 0 || fr.depth > 0) {
      kinds.add('Fins');
      const d = Math.max(fl.depth || 0, fr.depth || 0);
      const g = (fl.depth || 0) >= (fr.depth || 0) ? fl.gap : fr.gap;
      pfs.push(calcFinPF(d, g));
    }
  }
  if (!kinds.size) return { shading: 'None', pf: '-' };
  const shading = [...kinds].join(' + ');
  // The deepest shade is the one that governs, and a single number is what a reviewer checks.
  const pf = Math.max(...pfs);
  return { shading, pf: pf > 0 ? pf.toFixed(2) : '-' };
}

/**
 * The window specs that actually apply to each orientation, resolved through the cascade.
 *
 * This table used to pick its specs straight out of `envelope_defaults.orientationData[dir] ||
 * orientationOpenings[dir]`, so a project authored per FLOOR or per ZONE produced NO fenestration
 * rows at all - while the area figures beside them were already resolver-backed, leaving the
 * section silently disagreeing with itself. One wall per direction is representative: every wall
 * facing that way inherits the same level unless a zone overrides it, and the area columns are
 * summed from the full wall enumeration regardless of which wall is sampled here.
 */
function orientationWindowSpecs(b) {
  const out = {};
  // Authoritative where the walls exist: what each one actually resolves to.
  try {
    for (const { floor, zone, wallId, dir } of enumerateExteriorWalls(b)) {
      if (out[dir]) continue;
      const wins = (resolveWallEnvelope(b, floor, zone, wallId, dir).fen?.windows || []).filter(Boolean);
      if (wins.length) out[dir] = wins;
    }
  } catch { /* geometry too incomplete to enumerate */ }
  // A spec exists whether or not the walls have been drawn. Driving discovery off the wall
  // enumeration ALONE made this table vanish on a model with fenestration but no geometry yet,
  // so anything the enumeration could not reach is picked up from the declared containers.
  eachEnvelopeContainer(b, (od, dir) => {
    if (!dir || out[dir]) return;
    const raw = od?.fenestration?.windows ?? od?.windows;
    const wins = Array.isArray(raw) ? raw.filter(Boolean) : (raw ? [raw] : []);
    if (wins.length) out[dir] = wins;
  });
  return out;
}

/** Window-to-wall ratio and resolved glazing per orientation, from whichever level defines it. */
function fenestrationRatios(b) {
  const ed = b.envelope_defaults || {};
  const areas = orientationWindowAreas(b);
  const specs = orientationWindowSpecs(b);
  const rows = [];
  for (const dir of DIRS) {
    const wins = specs[dir];
    if (!wins?.length) continue;
    const glaz = wins.map((w) => resolveWindowGlazing(w, b.custom_constructions?.glazings));
    const { shading, pf } = orientationShading(wins);
    const winArea = areas[dir]?.win;
    const wallArea = areas[dir]?.wall;
    // WWR is an AREA ratio: window area on this orientation over gross exterior wall area on the
    // SAME orientation - the very quantities the Building total row sums across all orientations.
    // It used to add up the `wwr` numbers typed on each window spec, which ignored how much wall
    // an orientation actually has and missed windows given by explicit dimensions entirely (those
    // carry no `wwr` field, so an orientation could report real m² of glass against a blank ratio).
    // Where the geometry could not be enumerated there is no area to divide, so the specified
    // ratio is shown rather than blanking the column on an incomplete model.
    const specified = wins.reduce((s, w) => s + (Number(w.wwr) > 1 ? Number(w.wwr) / 100 : Number(w.wwr) || 0), 0);
    const ratio = (Number.isFinite(wallArea) && wallArea > 0 && Number.isFinite(winArea))
      ? winArea / wallArea
      : specified;
    rows.push({
      orientation: dir,
      // One decimal, matching the Building total row: both are now the same computed quantity.
      wwr: ratio > 0 ? `${(ratio * 100).toFixed(1)}%` : '-',
      area: Number.isFinite(winArea) && winArea > 0 ? group(winArea, 1) : '-',
      shading,
      pf,
      // No library id means the values were typed in directly, so name it "Custom glazing".
      glazing: sharedText(glaz, (g) => g.name) ?? (glaz.some((g) => g.u != null) ? 'Custom glazing' : '-'),
      u: sharedNum(glaz, (g) => g.u, (v) => num(v, 2)) ?? '-',
      shgc: sharedNum(glaz, (g) => g.shgc, (v) => num(v, 2)) ?? '-',
      vlt: sharedNum(glaz, (g) => g.vltPct, (v) => `${Math.round(v)}%`) ?? '-',
    });
  }
  if (!rows.length) return null;

  // Building level WWR: all glazing against all gross exterior wall. It is area weighted from the
  // enumerated walls rather than averaged across the rows above, because orientations differ in
  // wall area and a face with no windows still belongs in the denominator. The optical columns
  // stay blank: a single U or SHGC for the whole building would have to be invented.
  let totWall = 0;
  let totWin = 0;
  for (const d of Object.values(areas)) { totWall += d.wall || 0; totWin += d.win || 0; }
  if (totWall > 0) {
    rows.push({
      orientation: 'Building total',
      wwr: `${((totWin / totWall) * 100).toFixed(1)}%`,
      area: group(totWin, 1),
      glazing: '-', u: '-', shgc: '-', vlt: '-', shading: '-', pf: '-',
      _strong: true,
      _strongRule: true,
    });
  }

  return {
    id: 'fenestration',
    title: 'Fenestration by orientation',
    columns: [
      { key: 'orientation', label: 'Orientation' },
      { key: 'wwr', label: 'WWR', align: 'right' },
      { key: 'area', label: 'Window area (m²)', align: 'right' },
      { key: 'glazing', label: 'Glazing' },
      { key: 'u', label: 'U (W/m²K)', align: 'right' },
      { key: 'shgc', label: 'SHGC', align: 'right' },
      { key: 'vlt', label: 'VLT', align: 'right' },
      { key: 'shading', label: 'Shading' },
      { key: 'pf', label: 'PF', align: 'right' },
    ],
    rows,
  };
}

function constructions(b) {
  const cc = b.custom_constructions || {};
  const rows = [];
  for (const [family, list] of sortedEntries(cc)) {
    for (const c of (list || [])) {
      rows.push({
        // 'walls' -> 'Wall', so the family reads as a label beside the "Default wall" rows below.
        element: family.replace(/s$/, '').replace(/^./, (c) => c.toUpperCase()),
        name: c.name || c.id,
        u: num(c.computedU ?? c.u_value, 3) ?? '-',
        source: c.source === 'u_value' ? 'U-value' : 'Layers',
      });
    }
  }
  const ed = b.envelope_defaults || {};
  for (const [k, label] of [['wall_construction_id', 'Default wall'], ['roof_construction_id', 'Default roof'],
    ['floor_construction_id', 'Default floor'], ['partition_construction_id', 'Default partition']]) {
    if (ed[k]) rows.push({ element: label, name: String(ed[k]), u: '-', source: 'Library' });
  }
  if (!rows.length) return null;
  return {
    id: 'constructions',
    title: 'Constructions',
    columns: [
      { key: 'element', label: 'Element' },
      { key: 'name', label: 'Construction' },
      { key: 'u', label: 'U (W/m²K)', align: 'right' },
      { key: 'source', label: 'Defined by' },
    ],
    rows,
  };
}

function spaceGroups(b) {
  const groups = b.groups || [];
  if (!groups.length) return null;
  return {
    id: 'groups',
    title: 'Space groups',
    columns: [
      { key: 'name', label: 'Group' },
      { key: 'zones', label: 'Zones', align: 'right' },
    ],
    rows: groups.map((g) => ({ name: g.name || g.id, zones: String((g.zoneIds || []).length) })),
  };
}

function schedules(b) {
  const bt = b.building_type || 'office';
  const rows = SCHEDULE_TYPES.map((t) => {
    const r = resolveSchedule(t.id, b.schedules?.building, {}, {}, bt, b.schedule_code);
    const wd = r?.weekday || [];
    const peak = wd.length ? Math.max(...wd) : null;
    const active = wd.filter((v) => v >= (peak || 0) * 0.5).length;
    return {
      type: t.label,
      unit: t.unit,
      source: r?.source === 'default' ? 'Code default' : 'Override',
      peak: t.isSetpoint ? num(peak, 1) : (peak == null ? '-' : `${Math.round(peak * 100)}%`),
      active: `${active} h`,
    };
  });
  const custom = (b.custom_schedules || []).map((c) => ({
    type: `${c.name} (custom)`,
    unit: SCHEDULE_TYPE_MAP[c.typeId]?.unit || '',
    source: 'User defined',
    peak: `${Math.round(Math.max(...(c.weekday || [0])) * 100)}%`,
    active: '-',
  }));
  // Only the code the project is modelled on is cited. Naming both would suggest the model
  // was checked against two codes at once.
  const src = scheduleSource(bt, b.schedule_code);
  const cite = [src.label, src.section, src.table, src.space].filter(Boolean).join(' · ');
  return {
    id: 'schedules',
    title: 'Schedules',
    note: src.table ? cite : src.note,
    columns: [
      { key: 'type', label: 'Schedule' },
      { key: 'unit', label: 'Unit' },
      { key: 'source', label: 'Source' },
      { key: 'peak', label: 'Weekday peak', align: 'right' },
      { key: 'active', label: 'Active hours', align: 'right' },
    ],
    rows: [...rows, ...custom],
  };
}

/** The method definition for a load, which is where its unit lives. */
function loadMethod(def) {
  const t = LOAD_TYPES[def.type];
  return (t?.methods || []).find((m) => m.value === def.method) || null;
}

function internalLoads(b) {
  const defs = b.load_definitions || [];
  if (!defs.length) return null;
  return {
    id: 'loads',
    title: 'Internal loads',
    columns: [
      { key: 'name', label: 'Load' },
      { key: 'type', label: 'Type' },
      { key: 'target', label: 'Applies to' },
      { key: 'value', label: 'Value', align: 'right' },
      { key: 'unit', label: 'Unit' },
      { key: 'method', label: 'Method' },
    ],
    rows: [...defs]
      .sort((a, c) => String(a.type).localeCompare(String(c.type)) || String(a.name).localeCompare(String(c.name)))
      .map((d) => {
        const m = loadMethod(d);
        return {
          name: d.name || LOAD_TYPES[d.type]?.label || humanise(d.type),
          type: LOAD_TYPES[d.type]?.label || humanise(d.type),
          target: d.target?.kind === 'zone' ? 'Zone' : d.target?.kind === 'zonelist' ? 'Group' : d.target?.kind === 'facility' ? 'Facility' : 'Building',
          value: num(d.value, 2) ?? '-',
          unit: m?.unit || '',
          method: m?.label || humanise(d.method),
        };
      }),
  };
}

function hvac(b) {
  const sys = b.hvac_systems || [];
  if (!sys.length) return null;
  return {
    id: 'hvac',
    title: 'HVAC systems',
    columns: [
      { key: 'name', label: 'System' },
      { key: 'type', label: 'Type' },
      { key: 'zones', label: 'Zones served', align: 'right' },
      { key: 'cooling', label: 'Cooling COP', align: 'right' },
      { key: 'heating', label: 'Heating' },
    ],
    rows: sys.map((s) => {
      if (isDetailedSystem(s)) {
        const d = detailedSummary(s, b);
        return { name: s.name || s.id, type: d.label, zones: String((s.targets || []).length), cooling: num(d.coolingCop, 2) ?? '-', heating: d.heating || '-' };
      }
      return {
        name: s.name || s.id,
        type: getHvacType(s.type)?.label || s.type,
        zones: String((s.targets || []).length),
        cooling: num(s.params?.cooling_cop ?? s.params?.chiller_cop, 2) ?? '-',
        heating: s.params?.has_heating === false ? 'Electric resistance'
          : (s.params?.heating_type || (s.params?.has_heating ? 'Hot water boiler' : '-')),
      };
    }),
  };
}

function daylighting(b, daylight) {
  if (!daylight) return null;
  const rows = [];
  if (daylight.gridSpacing != null) rows.push({ item: 'Sensor grid spacing', value: `${num(daylight.gridSpacing, 2)} m` });
  if (daylight.workplaneHeight != null) rows.push({ item: 'Workplane height', value: `${num(daylight.workplaneHeight, 2)} m` });
  if (daylight.skyModel) rows.push({ item: 'Sky model', value: String(daylight.skyModel) });
  for (const [k, v] of sortedEntries(daylight.reflectances || {})) {
    rows.push({ item: `Reflectance, ${k}`, value: num(v, 2) ?? '-' });
  }
  if (!rows.length) return null;
  return {
    id: 'daylighting',
    title: 'Daylighting inputs',
    columns: [{ key: 'item', label: 'Input' }, { key: 'value', label: 'Value', align: 'right' }],
    rows,
  };
}

// ── public ────────────────────────────────────────────────────────────────────

/**
 * Every input group that has data, in report order.
 * @param {object} building  project.building
 * @param {object} [opts]    { daylight } extra wizard-specific inputs
 */
export function inputSummary(building, opts = {}) {
  const b = building || {};
  return [
    geometry(b),
    fenestrationRatios(b),
    constructions(b),
    spaceGroups(b),
    schedules(b),
    internalLoads(b),
    hvac(b),
    daylighting(b, opts.daylight),
  ].filter(Boolean);
}

/** The groups a given report type should print, in order. */
export function inputSummaryFor(reportType, building, opts = {}) {
  const all = inputSummary(building, opts);
  if (reportType !== 'daylight') return all;
  // A daylight run evaluates none of these, so printing them would imply they mattered.
  const drop = new Set(['schedules', 'loads', 'hvac']);
  return all.filter((g) => !drop.has(g.id));
}
